import { createModel } from '@rematch/core';
import { DATETIME_24H_FORMAT } from 'components/RevxDate';
import {
  JobAdjustmentViewer,
  JobCancellationParams,
  JobCancellationViewer,
  JobOriginalQuote,
} from 'domains/workspaceDomain';
import { isUndefined, sortBy } from 'lodash-es';
import { RootModel } from 'models';
import {
  highestRoundOf,
  nextBusinessDayEnd,
  nextOfferAvailable,
} from 'utils/rfmOffer';
import moment, { Moment } from 'moment';
import {
  default as jobService,
  default as workspaceService,
} from 'services/jobService';
import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import {
  PressAdjustment,
  RFMOfferParams,
  RFMOfferUI,
  RFMSupplierOrg,
  ShippingAdjustment,
  SkuAvlPresses,
  SpecIdToShippingMap,
} from './../domains/workspaceDomain';
import { baseReducers, BaseState } from './baseReducers';

interface JobAdjustState {
  visible: boolean;
  date?: Moment;
  /** freight the supplier is paid for */
  price?: number;
  /** freight on revshop's account, billed to the buyer */
  revshopPrice?: number;
  shippings?: SpecIdToShippingMap;
}

interface RfmState extends BaseState {
  jobOriginal?: JobOriginalQuote;
  offers?: RFMOfferUI[];
  nextOffer?: RFMOfferUI;
  avlPresses?: SkuAvlPresses[];
  focusRound: number;
  supplierSelectVisible: boolean;
  eligibleSuppliers?: RFMSupplierOrg[];
  jobAdjust: JobAdjustState;
}

const initialState: RfmState = {
  jobOriginal: undefined,
  offers: [],
  nextOffer: undefined,
  avlPresses: undefined,
  focusRound: 1,
  supplierSelectVisible: false,
  eligibleSuppliers: undefined,
  jobAdjust: {
    visible: false,
  },
};

/**
 * Re-derives the buyer price and the margin of the round being composed.
 *
 * The buyer price is a total that already carries the job's freight, exactly as the first offer
 * takes it from the job's revx charge, so freight on revshop's account replaces that component
 * rather than adding onto it. A supplier price the sourcer edits is passed on to the buyer, which
 * keeps the margin where the round started instead of eating the difference.
 */
const withMarkup = (cost: number, markupPct: number | null) =>
  cost + (cost * (markupPct || 0)) / 100;

const applyOfferPrices = (state: RfmState) => {
  const offer = state.nextOffer;
  if (!offer) {
    return;
  }
  const buyerBase = offer.buyerPriceBase ?? offer.buyerPrice;
  offer.buyerPriceBase = buyerBase;

  // What the supplier is paid never reaches the buyer's price: raising it comes out of the margin.
  // Freight revshop puts on its own account does reach it, at the carrier cost plus revshop's
  // shipping markup, replacing the freight the job was quoted with.
  const quotedFreight = state.jobOriginal?.revxFreightCharge || 0;
  const revshopFreight = offer.revshopShippingPrice || 0;
  const revxFreight = revshopFreight
    ? withMarkup(revshopFreight, offer.shippingMarkupPct)
    : quotedFreight;

  offer.buyerPrice = buyerBase - quotedFreight + revxFreight;
  // revshop buys the freight it puts on its own account, so that cost is part of the margin too
  offer.revshopMargin =
    offer.buyerPrice - offer.supplierPrice - revshopFreight;
};

export const rfm = createModel<RootModel>()({
  state: initialState,
  reducers: {
    ...baseReducers<RfmState>(),
    reset: () => initialState,
    updatePressAdjustment: (state, payload: PressAdjustment) => {
      if (state.nextOffer !== undefined) {
        const toUpdateIndex = state.nextOffer.pressAdjustments?.findIndex(
          (pa) => pa.skuId === payload.skuId
        );
        if (toUpdateIndex !== undefined && toUpdateIndex >= 0) {
          state.nextOffer.pressAdjustments![toUpdateIndex] = payload;
        }
      }
      return state;
    },
    updateNextOfferFields: (
      state,
      payload: {
        supplierPrice?: number;
        turnTimeDays?: number;
        expireAtFormatted?: string;
      }
    ) => {
      if (state.nextOffer === undefined) {
        return state;
      }
      if (payload.supplierPrice !== undefined) {
        state.nextOffer.supplierPrice = payload.supplierPrice;
      }
      if (payload.turnTimeDays !== undefined) {
        state.nextOffer.turnTimeDays = payload.turnTimeDays;
      }
      if (payload.expireAtFormatted !== undefined) {
        state.nextOffer.expireAtFormatted = payload.expireAtFormatted;
      }
      applyOfferPrices(state);
      return state;
    },
    createNextOffer: (state) => {
      // The draft is built here so the button above the table and the one in the row run the same
      // code instead of each carrying their own copy of the cycle rule.
      if (!nextOfferAvailable(state.offers, state.nextOffer)) {
        return state;
      }
      const highestRound = highestRoundOf(state.offers);
      const lastOffer = state.offers!.find(
        (offer) => offer.round === highestRound
      )!;
      const newExpireAt = nextBusinessDayEnd();
      state.nextOffer = {
        ...lastOffer,
        id: undefined,
        round: highestRound + 1,
        expireAt: newExpireAt,
        expireAtFormatted: moment(newExpireAt).format(DATETIME_24H_FORMAT),
        invitations: [],
      };
      state.focusRound = highestRound + 1;
      return state;
    },
    toggleJobAdjustVisible: (state) => {
      state.jobAdjust.visible = !state.jobAdjust.visible;
      return state;
    },
    resetJobAdjust: (state) => {
      state.jobAdjust.date = undefined;
      state.jobAdjust.shippings = undefined;
      state.jobAdjust.price = undefined;
      return state;
    },
    updateShipDate: (state, payload: Moment) => {
      state.jobAdjust.date = payload;
      return state;
    },
    updateShippingAdjustments: (state, payload: SpecIdToShippingMap) => {
      state.jobAdjust.shippings = payload;
      // Which account the freight sits on decides who pays it: the supplier ships it and is paid
      // for it, revshop ships it and bills the buyer, or the customer's own account is billed
      // outside the job altogether.
      let supplierPrice: number = 0;
      let revshopPrice: number = 0;
      for (const specId in payload) {
        const charge = payload[specId]?.shippingTotalCharge || 0;
        if (payload[specId]?.accountType === ShippingAccountTypeEnum.SUPPLIER) {
          supplierPrice += charge;
        } else if (payload[specId]?.accountType === ShippingAccountTypeEnum.REVSHOP) {
          revshopPrice += charge;
        }
      }
      state.jobAdjust.price = supplierPrice;
      state.jobAdjust.revshopPrice = revshopPrice;
      return state;
    },
    updateOfferWithShippingAdjustments: (state) => {
      if (
        !(
          isUndefined(state.nextOffer) ||
          isUndefined(state.jobAdjust.price) ||
          isUndefined(state.jobAdjust.shippings)
        )
      ) {
        let shippingAdjustments: ShippingAdjustment[] = [];
        for (const specId in state.jobAdjust.shippings) {
          shippingAdjustments.push({
            jobShippingItemSpecId: +specId,
            shipmentType: state.jobAdjust.shippings[specId].shipmentType,
            carrierService: state.jobAdjust.shippings[specId].carrierService,
            carrierType: state.jobAdjust.shippings[specId].carrierType,
            // the account comes with the chosen service, and decides who is billed for the freight
            accountType: state.jobAdjust.shippings[specId].accountType,
            charge: state.jobAdjust.shippings[specId].shippingTotalCharge,
            estimatedShipTime: state.jobAdjust.date?.toISOString() || '',
            estimatedDeliveryTime:
              state.jobAdjust.shippings[specId].deliveryDateTime,
          });
        }
        state.nextOffer.shippingAdjustments = shippingAdjustments;
        state.nextOffer.shippingPrice = state.jobAdjust.price;

        state.nextOffer.revshopShippingPrice = state.jobAdjust.revshopPrice || 0;
        applyOfferPrices(state);
      }

      return state;
    },
  },
  effects: (dispatch) => {
    const { rfm } = dispatch;
    return {
      async getRfmOffers(payload: number, state): Promise<void> {
        const offers = await workspaceService.rfmOffersByJobId(payload);
        const sortedOffers = sortBy(offers, 'round');
        const lastOffer = sortedOffers[sortedOffers.length - 1];
        rfm.setState({
          offers: sortedOffers.map((o) => ({
            ...o,
            expireAtFormatted: moment(o.expireAt).format(DATETIME_24H_FORMAT),
          })),
          focusRound: lastOffer.round,
          nextOffer: undefined,
          supplierSelectVisible: false,
          eligibleSuppliers: undefined,
          jobAdjust: {
            visible: false,
          },
        });
      },
      async nextRfmOffer(payload: RFMOfferParams, state): Promise<void> {
        await workspaceService.nextRfmOffer(payload);
        rfm.getRfmOffers(payload.jobId);
      },
      async getAvlPresses(payload: number, state): Promise<void> {
        const avlPresses = await workspaceService.availablePresses(payload);
        rfm.setState({ avlPresses });
      },
      async getEligibleSuppliers(payload: undefined, state): Promise<void> {
        if (state.rfm.nextOffer) {
          const eligibleSuppliers = await workspaceService.eligibleSuppliers(
            state.rfm.nextOffer.jobId,
            state.rfm.nextOffer.pressAdjustments
          );
          rfm.setState({ eligibleSuppliers });
        }
      },
      async resendInvitation(payload: number, state): Promise<void> {
        await workspaceService.resendInvitation(payload);
      },

      async cancelRfm(
        payload: { jobId: number; reason?: string },
        state
      ): Promise<void> {
        await workspaceService.cancelRFM(payload.jobId, {
          reason: payload.reason,
        });
        await rfm.getRfmOffers(payload.jobId);
      },

      async jobsAdjustmentsAll(jobId: string): Promise<JobAdjustmentViewer[]> {
        return await jobService.jobsAdjustmentsAll(jobId);
      },

      async getJobAdjustmentById({
        jobId,
        adjustmentId,
      }: {
        jobId: string;
        adjustmentId: number;
      }): Promise<JobAdjustmentViewer[]> {
        return await jobService.getJobAdjustmentById(jobId, adjustmentId);
      },

      async getJobCancellations(jobId: string): Promise<JobCancellationViewer> {
        return await jobService.getJobCancellations(jobId);
      },

      async createJobCancellations(
        jobId: string
      ): Promise<JobCancellationViewer> {
        return await jobService.createJobCancellations(jobId);
      },

      async updateJobCancellations({
        jobId,
        id,
        data,
      }: {
        jobId: string;
        id: number;
        data: Partial<JobCancellationParams>;
      }): Promise<JobCancellationViewer> {
        return await jobService.updateJobCancellations(jobId, id, data);
      },

      async acceptJobAdjustments({
        jobId,
        id,
      }: {
        jobId: number;
        id: number;
      }): Promise<JobAdjustmentViewer> {
        return await jobService.acceptJobAdjustments(jobId, id);
      },

      async submitJobAdjustments({
        jobId,
        id,
      }: {
        jobId: number;
        id: number;
      }): Promise<JobAdjustmentViewer> {
        return await jobService.submitJobAdjustments(jobId, id);
      },

      async cancelJobCancellations({
        jobId,
        id,
      }: {
        jobId: number;
        id: number;
      }): Promise<JobAdjustmentViewer> {
        return await jobService.cancelJobCancellations(jobId, id);
      },
    };
  },
});
